'use client';

import { NavArrowRight } from '@byway/icons';
import { notFound, useParams } from 'next/navigation';
import { Image, StructuredText } from 'react-datocms';
import { CardShell, CardShellContent, CardShellCTA, CardShellImage } from 'ui';
import { Accommodation } from '~/components/Accommodation';
import { Activity } from '~/components/Activity';
import { Breadcrumbs } from '~/components/Breadcrumbs';
import { Divider } from '~/components/Divider';
import { Stack } from '~/components/Layout/Stack';
import { Link } from '~/components/Link';
import { Location } from '~/components/Location';
import TravelInformationText from '~/components/TravelInformation/TravelInformationText';
import { useJourneyGuide } from '~/contexts/JourneyGuideContext';
import TimelineTransformer, {
  type StayTimelineItem,
  type StopTimelineItem
} from '~/transformers/Dato/TimelineTransformer';

export default function PlaceDetail() {
  const params = useParams<{ placeId: string; tripId: string }>();

  const tripId = params?.tripId;
  const placeId = params?.placeId;

  const [journeyGuide] = useJourneyGuide();
  const { timelineItems: items } = new TimelineTransformer(journeyGuide.itinerary?.items ?? []).transform();

  const item = items.find((item) => item.id === placeId) as StayTimelineItem | StopTimelineItem;
  if (!item) {
    return notFound();
  }

  const { activities, foodAndDrinkRecommendation, place, type } = item ?? {};

  const accommodationBookings = item.type === 'Stay' ? item.accommodationBookings : [];

  return (
    <div className="mb-48px">
      <div className="px-4 pt-4 bg-primary-100">
        <Breadcrumbs
          items={[
            {
              href: `/journey-guide/${tripId}#${placeId}`,
              label: 'Journey Guide'
            },
            {
              label: place?.placeName || '',
              isCurrent: true,
              href: '#'
            }
          ]}
        />
      </div>
      <CardShell>
        {place?.images[0]?.responsiveImage && (
          <CardShellImage>
            {/* eslint-disable-next-line jsx-a11y/alt-text */}
            <Image data={place.images[0].responsiveImage} className="print:hidden" />
          </CardShellImage>
        )}
        <CardShellContent>
          <Location location={item} />

          {type === 'Stay' && (
            <>
              <Divider variant="horizontal" />
              <h3 className="my-24px typography-chip-style text-neutral-600">Accommodation</h3>
              <Accommodation bookings={accommodationBookings} isDetailed />
            </>
          )}

          {activities && activities.length > 0 && (
            <>
              <div className="flex items-center border-t border-t-neutral-300">
                <h3 className="my-24px typography-chip-style text-neutral-600">Recommendations</h3>
              </div>
              <ul className="mt-24px">
                {activities.map((activity) => {
                  return (
                    <li key={`stay-activity-${activity.id}`} className="mb-24px" id={activity.id}>
                      <CardShell isElevated={false}>
                        <CardShellContent>
                          <Activity activity={activity} />
                        </CardShellContent>
                        <CardShellCTA>
                          <Link href={`/journey-guide/${tripId}/${placeId}/${activity.id}`} target="_self">
                            <Stack space="--by-space-050" variant="horizontal">
                              <span>Learn more</span>
                              <NavArrowRight decorative size="large" />
                            </Stack>
                          </Link>
                        </CardShellCTA>
                      </CardShell>
                    </li>
                  );
                })}
              </ul>
            </>
          )}
          {foodAndDrinkRecommendation && (
            <div className="border-t border-t-neutral-300 mt-24px">
              <h3 className="my-24px typography-chip-style text-neutral-600">Food and Drink in {place.placeName}</h3>
              <div className="typography-body-1 my-48px">
                <TravelInformationText data={foodAndDrinkRecommendation.description} />
              </div>
            </div>
          )}
        </CardShellContent>
      </CardShell>
    </div>
  );
}
